import { CurrentUser, UserPermissions } from 'types/CurrentUser';
import { UserFeatureFlagName } from 'types/UserFeatureFlags';
import localeFromBrowser from 'containers/App/lib/localeFromBrowser';
import { useFeatureOverrideStorage } from 'features/featureFeature/useFeatureOverrideStorage';
import { useCanOverrideFeatures } from 'features/featureFeature/useCanOverrideFeatures';

import { accessUserFeatureFlags, Awaitable } from './util';
import { useCurrentUser } from './baseState/useCurrentUser';

type AwaitableIsRole = Awaitable<boolean>;
type AwaitableUserFeatureFlag = Awaitable<boolean>;

type PermissionKey = keyof UserPermissions;

export const useCurrentUserAwaitableWithTransform: <TData>(
  transform: (t: CurrentUser) => TData
) => Awaitable<TData> = transform => {
  const currentUser = useCurrentUser();
  const canOverride = useCanOverrideFeatures();
  const { overrideUser } = useFeatureOverrideStorage();
  if (currentUser) {
    return {
      loading: false,
      data: transform(canOverride ? overrideUser(currentUser) : currentUser),
    };
  }
  return { loading: true };
};

export const useIsCurrentUserViewerAwaitable = (): AwaitableIsRole =>
  useCurrentUserAwaitableWithTransform(u => u.viewer);

const useIsCurrentUserAnalystAwaitable = (): AwaitableIsRole =>
  useCurrentUserAwaitableWithTransform(u => u.analyst);

const useIsCurrentUserAdminAwaitable = (): AwaitableIsRole =>
  useCurrentUserAwaitableWithTransform(u => u.superAdmin);

const useIsCurrentUserProgramLeadAwaitable = (): AwaitableIsRole =>
  useCurrentUserAwaitableWithTransform(u => u.programLead);

const useIsCurrentUserAiMarketAnalystAwaitable = (): AwaitableIsRole =>
  useCurrentUserAwaitableWithTransform(u => u.aiMarketAnalyst);

const useIsCurrentUserAiProgramLeadAwaitable = (): AwaitableIsRole =>
  useCurrentUserAwaitableWithTransform(u => u.aiProgramLead);

const useIsCurrentUserAiContextManagerAwaitable = (): AwaitableIsRole =>
  useCurrentUserAwaitableWithTransform(u => u.aiContextManager);

export const useHasUserFeatureAwaitable = (
  ff: UserFeatureFlagName
): AwaitableUserFeatureFlag =>
  useCurrentUserAwaitableWithTransform(u =>
    accessUserFeatureFlags(u).includes(ff)
  );

export const useIsCurrentUserViewer = (): boolean => {
  const awaitable = useIsCurrentUserViewerAwaitable();
  return awaitable.loading === false && awaitable.data;
};

export const useIsCurrentUserRegularUser = (): boolean => {
  const awaitable = useIsCurrentUserViewerAwaitable();
  return awaitable.loading === false && !awaitable.data;
};

export const useIsCurrentUserAdmin = (): boolean => {
  const awaitable = useIsCurrentUserAdminAwaitable();
  return awaitable.loading === false && awaitable.data;
};

export const useIsCurrentUserProgramLead = (): boolean => {
  const awaitable = useIsCurrentUserProgramLeadAwaitable();
  return awaitable.loading === false && awaitable.data;
};

export const useIsCurrentUserAiMarketAnalyst = (): boolean => {
  const awaitable = useIsCurrentUserAiMarketAnalystAwaitable();
  return awaitable.loading === false && awaitable.data;
};

export const useIsCurrentUserAiProgramLead = (): boolean => {
  const awaitable = useIsCurrentUserAiProgramLeadAwaitable();
  return awaitable.loading === false && awaitable.data;
};

export const useIsCurrentUserAiContextManager = (): boolean => {
  const awaitable = useIsCurrentUserAiContextManagerAwaitable();
  return awaitable.loading === false && awaitable.data;
};

export const useIsCurrentUserAnalyst = () => {
  const awaitable = useIsCurrentUserAnalystAwaitable();
  return awaitable.loading === false && awaitable.data;
};

export const useHasUserFeature = (ff: UserFeatureFlagName) => {
  const awaitable = useHasUserFeatureAwaitable(ff);
  return awaitable.loading === false && awaitable.data;
};

const useLocaleAwaitable = () =>
  useCurrentUserAwaitableWithTransform(u => u.locale);

export const useLocale = () => {
  const awaitable = useLocaleAwaitable();
  return awaitable.loading === false ? awaitable.data : localeFromBrowser();
};

const useUserPermissionAwaitable = () =>
  useCurrentUserAwaitableWithTransform(u => u.permissions);

export const useUserPermission = <T extends PermissionKey>(
  permissionKey: T
): boolean => {
  const awaitable = useUserPermissionAwaitable();
  const permissions = awaitable.loading === false && awaitable.data;

  if (permissions) {
    return permissions[permissionKey];
  }

  return false;
};
